import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import store from '@/store';

const LEVELS = ['debug', 'info', 'warning', 'error'];

// user id z Vuex store (backend ho síce doplní zo session, ale pridávame ho aj
// client-side kvôli párovaniu — vrátane masquerade intranet_id ak existuje).
// Čítané lazily vnútri funkcie, aby nevznikol problém s cyklickým importom.
function userContext() {
	try {
		const user = store && store.state && store.state.auth ? store.state.auth.user : null;
		if (!user) {
			return {};
		}
		const out = {};
		if (user.id != null) {
			out.user_id = user.id;
		}
		if (user.intranet_id != null) {
			out.intranet_id = user.intranet_id;
		}
		return out;
	} catch (e) {
		return {};
	}
}

// Fire-and-forget client logging na vlastný backend (POST /api/client-log).
// Nikdy nesmie hodiť — volá sa aj vnútri catch blokov.
export function clientLog(level, message, context = {}, source = null) {
	try {
		const payload = {
			level: LEVELS.includes(level) ? level : 'info',
			message: String(message).slice(0, 2000),
			source: source ? String(source).slice(0, 255) : null,
			context: {
				platform: Capacitor.getPlatform(),
				native: Capacitor.isNativePlatform(),
				...userContext(),
				...context,
			},
		};
		axios.post('/api/client-log', payload).catch(() => {}); // best-effort
	} catch (e) {
		// logging je best-effort, nikdy nepadá
	}
}

// convenience — .warn mapuje na backend level 'warning'
clientLog.debug = (m, c, s) => clientLog('debug', m, c, s);
clientLog.info = (m, c, s) => clientLog('info', m, c, s);
clientLog.warn = (m, c, s) => clientLog('warning', m, c, s);
clientLog.error = (m, c, s) => clientLog('error', m, c, s);

// bezpečné vytiahnutie info z axios erroru pre catch bloky
export function errorInfo(error) {
	return {
		status: (error && error.response && error.response.status) || null,
		message: (error && error.response && error.response.data && error.response.data.message)
			|| (error && error.message) || 'unknown',
	};
}

// errorInfo pripneme aj na clientLog, nech je všade dostupné cez jeden objekt
clientLog.errorInfo = errorInfo;

// zjednotené vytiahnutie message + stack z JS chyby (skrátený stack kvôli limitu 2000)
function jsErrorContext(error, extra = {}) {
	const err = error || {};
	return {
		error_message: (err && err.message) || String(err),
		error_name: err && err.name ? err.name : null,
		stack: err && err.stack ? String(err.stack).slice(0, 1500) : null,
		url: (typeof window !== 'undefined' && window.location) ? window.location.href : null,
		...extra,
	};
}

// Globálne zachytenie JS chýb na ktorých appka reálne padá:
// - Vue.config.errorHandler → chyby v render/lifecycle/watcher (napr. render crash detail boxu na null dátach)
// - window 'error' → nezachytené synchronné chyby
// - window 'unhandledrejection' → nezachytené promise rejecty
export function installGlobalErrorLogging(Vue) {
	try {
		const prevHandler = Vue.config.errorHandler;
		Vue.config.errorHandler = (err, vm, info) => {
			clientLog('error', 'vue error', jsErrorContext(err, {
				component: vm && vm.$options && vm.$options.name ? vm.$options.name : null,
				lifecycle_hook: info || null,
			}), 'js.vue');
			if (typeof prevHandler === 'function') {
				prevHandler(err, vm, info);
			} else if (process.env.NODE_ENV !== 'production') {
				// v dev nechaj chybu vypísať do konzoly
				// eslint-disable-next-line no-console
				console.error(err);
			}
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('error', (event) => {
				const err = event && event.error ? event.error : null;
				clientLog('error', 'window error', jsErrorContext(err, {
					error_message: (err && err.message) || (event && event.message) || 'unknown',
					source_file: event && event.filename ? event.filename : null,
					line: event && event.lineno != null ? event.lineno : null,
					col: event && event.colno != null ? event.colno : null,
				}), 'js.window');
			});

			window.addEventListener('unhandledrejection', (event) => {
				const reason = event ? event.reason : null;
				clientLog('error', 'unhandled promise rejection', jsErrorContext(reason, {
					error_message: (reason && reason.message) || String(reason),
				}), 'js.promise');
			});
		}
	} catch (e) {
		// inštalácia handlerov nikdy nesmie zhodiť boot appky
	}
}

clientLog.installGlobalErrorLogging = installGlobalErrorLogging;

export default clientLog;
